import { ApolloClient, InMemoryCache } from "@apollo/client/core"
import { setContext } from "@apollo/client/link/context"
import { ApolloLink } from "@apollo/client/link/core"
import { onError } from "@apollo/client/link/error"
import { Observable, getOperationDefinition } from "@apollo/client/utilities"
import createUploadLink from "apollo-upload-client/createUploadLink.mjs"

import searchPossibleTypes from "./graphql/possibleSearchTypes_generated.json"
import possibleTypes from "./graphql/possibleTypes_generated.json"
import { i18n } from "./i18n"
import { useSessionStore } from "./store/session"
import { notifyToast } from "./utils/graphqlToastNotifier"
import { TOAST_LIFE } from "./utils/misc"

const successTimeDelay = 150

let inFlightMutations = 0
let pendingSuccessToast = false
let successToastTimer: ReturnType<typeof setTimeout> | null = null

function onMutationStart() {
  inFlightMutations++
  if (successToastTimer) {
    clearTimeout(successToastTimer)
    successToastTimer = null
  }
}

function onMutationSettled() {
  inFlightMutations = inFlightMutations - 1
  if (inFlightMutations > 0 || !pendingSuccessToast) return

  successToastTimer = setTimeout(() => {
    successToastTimer = null
    if (inFlightMutations === 0 && pendingSuccessToast) {
      pendingSuccessToast = false
      notifyToast({
        severity: "success",
        summary: i18n.global.t("text.saved_successfully"),
        life: TOAST_LIFE,
      })
    }
  }, successTimeDelay)
}

export enum ApolloFetchPolicy {
  CacheFirst = "cache-first",
  CacheAndNetwork = "cache-and-network",
  NetworkOnly = "network-only",
}

const httpLink = createUploadLink({
  uri: import.meta.env.DEV
    ? "http://localhost:8080/api/graphql"
    : import.meta.env.BASE_URL + "/api/graphql",
})

const authLink = setContext(async (_, { headers }) => {
  const sessionStore = useSessionStore()
  const token = await sessionStore.getToken(false)
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  }
})

/** Error Handling */
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const skipToast = operation.getContext().skipGlobalErrorToast === true
  if (skipToast) return

  const summary = i18n.global.t("text.error")
  const detail = i18n.global.t("text.network_error")

  /** Network errors */
  if (networkError) {
    notifyToast({
      severity: "error",
      summary,
      detail,
      life: TOAST_LIFE,
    })

    return
  }

  /** GraphQL errors */
  if (graphQLErrors?.length) {
    const first = graphQLErrors[0]
    const identifier = (first.extensions?.identifier as string) ?? ""
    const detail = i18n.global.t(`errors.${identifier}`, "")
    notifyToast({
      severity: "error",
      summary,
      detail: detail,
      life: TOAST_LIFE,
    })
  }
})

/** Success Handling */
const successToastLink = new ApolloLink((operation, forward) => {
  const def = getOperationDefinition(operation.query)
  const tracked = def?.operation === "mutation" && !operation.getContext().skipGlobalSuccessToast

  if (tracked) onMutationStart()

  return new Observable((observer) => {
    const sub = forward(operation).subscribe({
      next(result) {
        if (tracked && !result.errors?.length) {
          pendingSuccessToast = true
        }
        observer.next(result)
      },
      error(err) {
        if (tracked) onMutationSettled()
        observer.error(err)
      },
      complete() {
        if (tracked) onMutationSettled()
        observer.complete()
      },
    })
    return () => sub.unsubscribe()
  })
})

const mainLink = errorLink.concat(
  successToastLink.concat(authLink.concat(httpLink as unknown as ApolloLink))
)

export const apolloClient = new ApolloClient({
  devtools: {
    enabled: import.meta.env.DEV,
    name: "adebisKita Main",
  },
  link: mainLink,
  cache: new InMemoryCache({
    possibleTypes,
    typePolicies: {
      Address: {
        merge: true,
      },
      AccountingGroups: {
        merge: true,
      },
      Birth: {
        merge: true,
      },
      Category: {
        merge: true,
      },
      Child: {
        merge: true,
      },
      Booking: {
        fields: {
          typeObject: {
            merge(_, incoming) {
              return incoming
            },
          },
          timeFactor: {
            merge: true,
          },
          times: {
            merge(_, incoming) {
              return incoming
            },
          },
          schoolData: {
            merge(_, incoming) {
              return incoming
            },
          },
        },
      },
      Contract: {
        merge: true,
        fields: {
          state: {
            merge(_, incoming = []) {
              return incoming
            },
          },
        },
      },
      Commune: {
        merge: true,
      },
      Contact: {
        merge: true,
      },
      ContactPerson: {
        merge: true,
      },
      Customer: {
        merge: true,
      },
      Employee: {
        merge: true,
      },
      EmployeeGroup: {
        merge: true,
      },
      Employment: {
        merge: true,
      },
      FoodType: {
        merge: true,
      },
      FeeType: {
        merge: true,
        fields: {
          contributionRates: {
            merge(_, incoming = []) {
              return incoming
            },
          },
        },
      },
      Group: {
        merge: true,
        fields: {
          managements: {
            merge(_, incoming = []) {
              return incoming
            },
          },
        },
      },
      Institution: {
        merge: true,
      },
      Movement: {
        merge: true,
      },
      Organization: {
        merge: true,
      },
      OrganizationUnit: {
        merge: true,
      },
      Person: {
        merge: true,
      },
      Qualification: {
        merge: true,
      },
      Registration: {
        merge: true,
      },
      Role: {
        fields: {
          permissions: {
            merge(_, incoming) {
              return incoming
            },
          },
        },
      },
      School: {
        merge: true,
      },
      SectionTime: {
        merge: true,
      },
      ServiceType: {
        merge: true,
      },
      SimpleAddress: {
        merge: true,
      },
      TimeRange: {
        merge: true,
      },
      Times: {
        merge: true,
      },
      Vaccination: {
        merge: true,
      },
      YouthWelfareOffice: {
        merge: true,
      },

      ProjectInstitution: {
        fields: {
          banks: {
            merge(_, incoming = []) {
              return incoming
            },
          },
        },
      },
      Appointment: {
        fields: {
          employees: {
            merge(_, incoming = []) {
              return incoming
            },
          },
        },
      },
      User: {
        keyFields: ["userId"],
      },
    },
  }),
})

const searchHttpLink = createUploadLink({
  uri: import.meta.env.DEV
    ? "http://localhost:8080/api/search"
    : import.meta.env.BASE_URL + "/api/search",
  fetch: ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init)) as never,
})

export const searchApolloClient = new ApolloClient({
  devtools: {
    enabled: import.meta.env.DEV,
    name: "adebisKita Search",
  },
  link: authLink.concat(searchHttpLink as unknown as ApolloLink),
  cache: new InMemoryCache({
    possibleTypes: searchPossibleTypes,
  }),
})
